import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang, fmtPrice } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { ProductDialog } from "@/components/ProductDialog";
import { Seo } from "@/components/Seo";
import { Pic } from "@/components/Pic";
import { PAGES, pageJsonld, productSeo, urlSlug } from "@/seo.mjs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all" }, { key: "kits" }, { key: "voladores", parent: "kits" }, { key: "reptadores", parent: "kits" },
  { key: "rodadores", parent: "kits" }, { key: "saltadores", parent: "kits" },
  { key: "motricidad", parent: "kits" }, { key: "juegos", parent: "kits" },
  { key: "figuras" }, { key: "hogar" }, { key: "social" },
];

export default function Catalogo() {
  const { t, lang, currency } = useLang();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const selected = slug ? products.find((p) => urlSlug(p.slug) === slug) : null;

  useEffect(() => {
    if (slug && loaded && products.length && !selected) {
      toast.error(t.catalog.notFound);
      navigate("/catalogo", { replace: true });
    }
  }, [slug, loaded, products, selected, navigate, t]);

  const visible = products.filter((p) => {
    if (filter === "all") return true;
    const f = FILTERS.find((x) => x.key === filter);
    return f?.parent ? p.subcategory === filter : p.category === filter;
  });

  const seo = selected ? productSeo(selected) : { ...PAGES.catalogo, jsonld: pageJsonld("catalogo") };

  return (
    <div data-testid="catalog-page" className="pt-16 min-h-screen">
      <Seo data={{ ...seo, type: selected ? "product" : "website" }} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Reveal>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{t.catalog.title}</h1>
          <p className="text-[#5C5C5C] mt-4 max-w-lg leading-relaxed">{t.catalog.sub}</p>
        </Reveal>
        <div className="flex flex-wrap gap-2 mt-10 mb-12">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`rounded-full px-4 py-2 text-xs md:text-sm font-semibold transition-colors duration-200 ${
                filter === f.key ? "bg-[#2E5A3B] text-white" : "bg-[#EFECE6] text-[#5C5C5C] hover:bg-[#2E5A3B]/10"
              } ${f.parent ? "border border-dashed border-[#2E5A3B]/30" : ""}`}>
              {t.catalog[f.key]}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
          {visible.map((p, i) => {
            const wide = i % 5 === 0;
            return (
              <Reveal key={p.id} delay={0.05 * (i % 6)} className={wide ? "col-span-2 lg:col-span-1" : ""}>
                <a href={`/catalogo/${urlSlug(p.slug)}`} data-testid={`product-card-${p.slug}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/catalogo/${urlSlug(p.slug)}`); }}
                  className="group text-left w-full block">
                  <div className={`overflow-hidden rounded-2xl bg-[#EFECE6] ${wide ? "aspect-[16/10] lg:aspect-square" : "aspect-square"}`}>
                    <Pic src={p.image} alt={`${p.name_es} impreso en 3D con material reciclado`}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="mt-3 px-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <h2 className="font-serif-d text-lg md:text-xl font-semibold text-[#132A1D] leading-tight">{lang === "es" ? p.name_es : p.name_en}</h2>
                      <p className="text-sm font-bold text-[#2E5A3B] whitespace-nowrap">{fmtPrice(p, currency) || t.catalog.free}</p>
                    </div>
                    <p className="text-xs text-[#5C5C5C] mt-1 uppercase tracking-wider">{p.material}</p>
                  </div>
                </a>
              </Reveal>
            );
          })}
        </div>
      </div>
      <ProductDialog product={selected} open={!!selected} onClose={() => navigate("/catalogo")} />
    </div>
  );
}
